<script lang="ts" setup>
    import '../../../css/choice.css';
    import { onMounted, reactive } from 'vue';
    import { initPinia } from '../../lib/PiniaMgr';
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore' 
    import { AppScreens } from "../../Enums/ChoiceEnums";
    import { IQuickPayRxHistoryItem, IQuickPayRxPharmacyItem } from '../../lib/Models/QuickPayModel';
    import { ModalSize } from "../../Enums/UiEnums";
    import { ICol } from "../../lib/ChoiceTableHelpers";
    import { ModalStyles, popAlert } from "../../lib/Notification";
    import { IToolbar, ToolbarStyle, ToolbarBackground, ToolbarPadding } from "../../lib/Toolbar" 
    import { ActionButtonStyle } from "../../lib/Types/Types";
    import Toolbar from "../../components/Toolbar.vue"
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import ModalDialog from '../../components/ModalDialog.vue'
    import * as ChoiceApi from '../../lib/ChoiceApi';

    const privilegesStore = usePrivilegesStore(initPinia());

    const props = defineProps<{
        patientId: number
    }>();

    const formData = reactive<{
        historyList: IQuickPayRxHistoryItem[],
        pharmacyList: IQuickPayRxPharmacyItem[],
        selectedPharmacy?: IQuickPayRxPharmacyItem,
        showPharmacy: boolean,
    }>({
        historyList: [],
        pharmacyList: [],
        showPharmacy: false
    });

    const toolbarConfig = reactive<IToolbar>({
        toolbarList: [
            {identifier: "", label: "", tooltip: "Refresh",  action: ActionButtonStyle.Refresh, performAction: () => onRefresh()},
        ]
    }); 

    const historyColumns = reactive<ICol[]>([
        { Name: 'Method', DataField: 'methodDesc' },
        { Name: 'Status', DataField: 'statusDesc' },
        { Name: 'Action', DataField: 'action' },
        { Name: 'Start Date', DataField: 'startDate' },
        { Name: 'Drug and Dosage', DataField: 'drugName' },
        { Name: 'Refilled', DataField: 'refillDate' },
        { Name: 'E-Status', DataField: 'communicationStatus' },
        { Name: 'Pharmacy', DataField: '' },
    ]);

    async function onRefresh() {
        const result = await ChoiceApi.quickPay.RxHistoryList(props.patientId);
        formData.historyList = result?.historyList ?? [];
        formData.pharmacyList = result?.pharmacyList ?? [];
    }

    async function getPrivileges() {
        await privilegesStore.getScreens([AppScreens.CheckInOutDashboard]);
    }

    async function fetchData() {
        onRefresh();
        getPrivileges();
    }
    fetchData();

    onMounted(async () => {
    });

    function openPharmacy(pharmacyId: number, surescriptsPharmacy: boolean) {
        formData.selectedPharmacy = formData.pharmacyList.find(item => item.pharmacyId == pharmacyId && item.surescriptsPharmacy == surescriptsPharmacy)
        if (!formData.selectedPharmacy) {
            popAlert("Pharmacy not found.", "Open Pharmacy", ModalStyles.warning);
            return;
        }
        formData.showPharmacy = true;
    }

    async function onHidePharmacy() {
        formData.showPharmacy = false;
    }

</script>

<template>
    <div class="row">
        <div class="col">
            <Toolbar 
                :configuration="toolbarConfig" 
                :toolbarStyle="ToolbarStyle.outline" 
                :toolbarBackground="ToolbarBackground.white"
                :toolbarPadding="ToolbarPadding.paddingOnlyLeftRight"/>                    
        </div>
    </div>
    <div class="row mt-2">
        <div class="col">
            <ChoiceTable 
                :columns="historyColumns" 
                :data="formData.historyList" 
                :show-filters="false" 
                :allowSorting="false"
                fontSize="smaller">
                <template #cell="{col, row, value}">
                    <template v-if="'Pharmacy' === col.Name">
                        <span class="isalus-link" title="Click to display pharmacy information" @click="() => openPharmacy(row.pharmacyId, row.surescriptsPharmacy)">
                            {{ row.pharmacyName }} 
                        </span>
                    </template>
                </template>
            </ChoiceTable>
        </div>
    </div>
    <ModalDialog :show="formData.showPharmacy" :size="ModalSize.Small" @hide="onHidePharmacy">
        <template #title>{{formData.selectedPharmacy?.pharmacyName}}</template>
        <div class="row">
            <div class="col label-isalus-small">
                {{ formData.selectedPharmacy?.address1 }}
            </div>
            <div class="w-100"></div>
            <div class="col label-isalus-small">
                {{ formData.selectedPharmacy?.address2 }}
            </div>
            <div class="w-100"></div>
            <div class="col label-isalus-small">
                {{ formData.selectedPharmacy?.city }}, {{ formData.selectedPharmacy?.state }} {{ formData.selectedPharmacy?.zipCode }}
            </div>
            <div class="w-100 mt-2"></div>
            <div class="col-3 label-isalus-small isalus-label-secondary">
                phone
            </div>
            <div class="col label-isalus-small">
                {{ formData.selectedPharmacy?.phoneWork }}
            </div>
            <div class="w-100 mt-2"></div>
            <div class="col-3 label-isalus-small isalus-label-secondary">
                fax
            </div>
            <div class="col label-isalus-small">
                {{ formData.selectedPharmacy?.phoneFax }}
            </div>
        </div>
    </ModalDialog>
</template>

